import { apiClient } from './client';
import {
  Certificate,
  CertificateAttachment,
  CertificateReview,
  CertificateReviewPayload,
  CertificateUpdatePayload,
} from '../types/product';

export async function fetchCertificates(keyword?: string): Promise<Certificate[]> {
  const { data } = await apiClient.get<Certificate[]>('/certificates', {
    params: keyword?.trim() ? { q: keyword.trim() } : {},
  });
  return data;
}

export interface CertificateCalendarItem {
  kind: string;
  date: string;
  certificateId: number;
  title?: string | null;
  validTo?: string | null;
  warnDays?: number | null;
}

export async function fetchCertificateCalendar(
  from: string,
  to: string
): Promise<CertificateCalendarItem[]> {
  const { data } = await apiClient.get<CertificateCalendarItem[]>('/certificates/calendar', {
    params: { from, to },
  });
  return data;
}

export async function fetchCertificate(certificateId: number): Promise<Certificate> {
  const { data } = await apiClient.get<Certificate>(`/certificates/${certificateId}`);
  return data;
}

export async function uploadCertificate(
  files: File[],
  options?: {
    title?: string;
    scopes?: string[];
    validFrom?: string;
    validTo?: string;
    warnDays?: number;
    content?: string;
  }
): Promise<Certificate> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  if (options?.title?.trim()) formData.append('title', options.title.trim());
  const scopes = (options?.scopes ?? []).map((s) => s.trim()).filter(Boolean);
  if (scopes.length > 0) {
    formData.append('scopeText', scopes.join(','));
    scopes.forEach((scope) => formData.append('scopes', scope));
  }
  if (options?.validFrom) formData.append('validFrom', options.validFrom);
  if (options?.validTo) formData.append('validTo', options.validTo);
  if (options?.warnDays != null && Number.isFinite(options.warnDays)) {
    formData.append('warnDays', String(options.warnDays));
  }
  if (options?.content?.trim()) formData.append('content', options.content.trim());
  const { data } = await apiClient.post<Certificate>('/certificates', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function updateCertificate(
  certificateId: number,
  payload: CertificateUpdatePayload
): Promise<Certificate> {
  const { data } = await apiClient.patch<Certificate>(`/certificates/${certificateId}`, payload);
  return data;
}

export async function uploadCertificateAttachments(
  certificateId: number,
  files: File[]
): Promise<CertificateAttachment[]> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const { data } = await apiClient.post<CertificateAttachment[]>(
    `/certificates/${certificateId}/attachments`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteCertificateAttachment(
  certificateId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/certificates/${certificateId}/attachments/${attachmentId}`);
}

export async function downloadCertificateAttachment(
  certificateId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/certificates/${certificateId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

/** 대표(첫) 파일 다운로드 — 레거시 호환 */
export async function downloadCertificate(
  certificateId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(`/certificates/${certificateId}/download`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function deleteCertificate(certificateId: number): Promise<void> {
  await apiClient.delete(`/certificates/${certificateId}`);
}

export async function fetchCertificateReviews(
  certificateId: number
): Promise<CertificateReview[]> {
  const { data } = await apiClient.get<CertificateReview[]>(
    `/certificates/${certificateId}/reviews`
  );
  return data;
}

export async function createCertificateReview(
  certificateId: number,
  payload: CertificateReviewPayload
): Promise<CertificateReview> {
  const { data } = await apiClient.post<CertificateReview>(
    `/certificates/${certificateId}/reviews`,
    payload
  );
  return data;
}

export async function updateCertificateReview(
  certificateId: number,
  reviewId: number,
  payload: Partial<CertificateReviewPayload>
): Promise<CertificateReview> {
  const { data } = await apiClient.patch<CertificateReview>(
    `/certificates/${certificateId}/reviews/${reviewId}`,
    payload
  );
  return data;
}

export async function deleteCertificateReview(
  certificateId: number,
  reviewId: number
): Promise<void> {
  await apiClient.delete(`/certificates/${certificateId}/reviews/${reviewId}`);
}

export async function fetchProductCertificates(productId: number): Promise<Certificate[]> {
  const { data } = await apiClient.get<Certificate[]>(`/products/${productId}/certificates`);
  return data;
}

export async function linkProductCertificate(
  productId: number,
  certificateId: number
): Promise<Certificate> {
  const { data } = await apiClient.post<Certificate>(`/products/${productId}/certificates`, {
    certificateId,
  });
  return data;
}

export async function unlinkProductCertificate(
  productId: number,
  certificateId: number
): Promise<void> {
  await apiClient.delete(`/products/${productId}/certificates/${certificateId}`);
}
